import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Curso } from '../types/usuario.types';
import { cursoService } from '../services/cursoService';
import { useCompetencias } from '../hooks/useCompetencias';
import { useConfigInstitucional } from '../hooks/useConfigInstitucional';
import { useAuth } from 'contexts/AuthContext';
import { CompetenciaPayload } from '../types/competencia.types';
import CursoFormPage from './CursoFormPage';

// ============================================================
// CursosPage – Lista de cursos con CRUD completo
// ============================================================

type Vista = 'list' | 'new' | 'edit' | 'competencias' | 'ver-competencias';

// ── Íconos compactos ──────────────────────────────────────────
const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);
const D_PLUS   = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_SEARCH = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_EDIT   = 'M5.433 13.917l1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65ZM3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z';
const D_DEL    = 'M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z';
const D_LIST   = 'M3 4.75a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM6.75 4a.75.75 0 0 0 0 1.5h10.5a.75.75 0 0 0 0-1.5H6.75ZM3 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm3.75-.75a.75.75 0 0 0 0 1.5h10.5a.75.75 0 0 0 0-1.5H6.75ZM3 15.25a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm3.75-.75a.75.75 0 0 0 0 1.5h10.5a.75.75 0 0 0 0-1.5H6.75Z';

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const BTN_COLOR: Record<string, string> = {
  blue:   'hover:text-blue-600   hover:bg-blue-50',
  red:    'hover:text-red-500    hover:bg-red-50',
  purple: 'hover:text-purple-600 hover:bg-purple-50',
};

const ActionBtn: React.FC<{ title: string; color: string; onClick: () => void; children: React.ReactNode }> =
  ({ title, color, onClick, children }) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded-lg text-slate-400 transition-colors ${BTN_COLOR[color]}`}>
      {children}
    </button>
  );

// ── Panel de competencias (paso 2 tras crear, o vista desde tabla) ─
interface Paso2Props {
  cursoId:     number;
  cursoNombre: string;
  onFinish:    () => void;
  onBack?:     () => void;
  readOnly?:   boolean;
  modo?:       'crear' | 'ver';
}

const PasoCompetencias: React.FC<Paso2Props> = ({
  cursoId, cursoNombre, onFinish, onBack, readOnly = false, modo = 'crear',
}) => {
  const { competencias, loading, saving, saveError, crear, actualizar, eliminar } = useCompetencias(cursoId);
  const [nombre, setNombre]         = useState('');
  const [descripcion, setDesc]      = useState('');
  const [ordenInput, setOrdenInput] = useState(1);
  const [errorLocal, setErrorLocal] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Mantener el campo orden apuntando al siguiente disponible
  useEffect(() => {
    setOrdenInput(competencias.length + 1);
  }, [competencias.length]);

  const handleAgregar = async () => {
    if (!nombre.trim()) { setErrorLocal('El nombre es requerido'); return; }
    setErrorLocal('');
    const payload: CompetenciaPayload = {
      cursoId,
      nombre:      nombre.trim(),
      descripcion: descripcion.trim() || undefined,
      orden:       ordenInput > 0 ? ordenInput : competencias.length + 1,
    };
    await crear(payload);
    setNombre(''); setDesc('');
    inputRef.current?.focus();
  };

  const handleMover = async (idx: number, dir: -1 | 1) => {
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= competencias.length) return;
    const a = competencias[idx];
    const b = competencias[targetIdx];
    // Swap de órdenes: primero uno, luego el otro para que el hook re-ordene entre calls
    await actualizar(a.idCompetencia, { cursoId, nombre: a.nombre, descripcion: a.descripcion ?? undefined, orden: b.orden });
    await actualizar(b.idCompetencia, { cursoId, nombre: b.nombre, descripcion: b.descripcion ?? undefined, orden: a.orden });
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">

      {/* Nav: breadcrumb al crear, botón volver al ver */}
      {modo === 'crear' ? (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <span className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-[10px]">✓</span>
            Datos del curso
          </span>
          <span className="text-slate-200">›</span>
          <span className="flex items-center gap-1 font-semibold text-blue-700">
            <span className="w-5 h-5 rounded-full bg-blue-700 text-white flex items-center justify-center font-bold text-[10px]">2</span>
            Competencias
          </span>
        </div>
      ) : (
        <button
          onClick={onBack ?? onFinish}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
          </svg>
          Volver a cursos
        </button>
      )}

      {/* Cabecera */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Competencias del curso</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          {modo === 'crear'
            ? <>Agrega las competencias para <span className="font-semibold text-slate-600">{cursoNombre}</span>. Puedes omitir este paso y configurarlas después.</>
            : <span className="font-semibold text-slate-600">{cursoNombre}</span>
          }
        </p>
      </div>

      {/* Formulario de nueva competencia — solo si no es readOnly */}
      {!readOnly && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
            <h2 className="text-white font-bold">Nueva competencia</h2>
            <p className="text-blue-200 text-xs mt-0.5">Capacidad o desempeño que el estudiante debe lograr</p>
          </div>
          <div className="p-6 space-y-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  value={nombre}
                  onChange={e => { setNombre(e.target.value); setErrorLocal(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleAgregar()}
                  placeholder="Ej: Resuelve problemas de cantidad"
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500
                    ${errorLocal ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                />
                {errorLocal && <p className="text-red-500 text-xs mt-1">{errorLocal}</p>}
              </div>
              <div className="w-20 flex-shrink-0">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">
                  Orden
                </label>
                <input
                  type="number"
                  min={1}
                  value={ordenInput}
                  onChange={e => setOrdenInput(Math.max(1, Number(e.target.value)))}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 text-center"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">
                Descripción (opcional)
              </label>
              <input
                type="text"
                value={descripcion}
                onChange={e => setDesc(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAgregar()}
                placeholder="Detalle adicional de la competencia"
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {saveError && <p className="text-red-500 text-xs">{saveError}</p>}
            <button
              onClick={handleAgregar}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {saving
                ? <SpinnerIcon />
                : <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"/></svg>
              }
              Agregar competencia
            </button>
          </div>
        </div>
      )}

      {/* Lista de competencias */}
      {loading
        ? <div className="flex justify-center py-6"><SpinnerIcon /></div>
        : competencias.length === 0
          ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-10 text-center">
              <p className="text-slate-400 text-sm">Sin competencias registradas para este curso</p>
            </div>
          )
          : (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                <span className="text-sm font-bold text-slate-700">Competencias registradas</span>
                <span className="text-xs text-slate-400">{competencias.length} total</span>
              </div>
              <ul className="divide-y divide-slate-50">
                {competencias.map((c, idx) => (
                  <li key={c.idCompetencia} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors">
                    {/* Número de orden */}
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                      {c.orden}
                    </span>

                    {/* Nombre + descripción */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{c.nombre}</p>
                      {c.descripcion && <p className="text-xs text-slate-400 mt-0.5 truncate">{c.descripcion}</p>}
                    </div>

                    {/* Controles: mover ↑↓ y eliminar (solo editable) */}
                    {!readOnly && (
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                          onClick={() => handleMover(idx, -1)}
                          disabled={saving || idx === 0}
                          className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-30"
                          title="Subir"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M10 17a.75.75 0 0 1-.75-.75V5.612L5.29 9.77a.75.75 0 0 1-1.08-1.04l5.25-5.5a.75.75 0 0 1 1.08 0l5.25 5.5a.75.75 0 1 1-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0 1 10 17Z" clipRule="evenodd"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => handleMover(idx, 1)}
                          disabled={saving || idx === competencias.length - 1}
                          className="p-1 rounded text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-30"
                          title="Bajar"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v10.638l3.96-4.158a.75.75 0 1 1 1.08 1.04l-5.25 5.5a.75.75 0 0 1-1.08 0l-5.25-5.5a.75.75 0 1 1 1.08-1.04l3.96 4.158V3.75A.75.75 0 0 1 10 3Z" clipRule="evenodd"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => eliminar(c.idCompetencia)}
                          disabled={saving}
                          className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40 ml-1"
                          title="Eliminar competencia"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.52.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )
      }

      {/* Footer */}
      <div className="flex justify-between items-center pt-2">
        {modo === 'crear' ? (
          <>
            <p className="text-xs text-slate-400">
              Puedes editar estas competencias en cualquier momento desde el listado de cursos.
            </p>
            <button
              onClick={onFinish}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-bold transition-colors shadow-sm"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
              </svg>
              {competencias.length > 0 ? 'Finalizar' : 'Omitir y finalizar'}
            </button>
          </>
        ) : (
          <button
            onClick={onBack ?? onFinish}
            className="px-5 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cerrar
          </button>
        )}
      </div>
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────
const CursosPage: React.FC = () => {
  const [vista, setVista]   = useState<Vista>('list');
  const [editId, setEditId] = useState<number | null>(null);
  const [cursoCreadoId, setCursoCreadoId]         = useState<number | null>(null);
  const [cursoCreadoNombre, setCursoCreadoNombre] = useState('');
  const [cursoVistaId, setCursoVistaId]           = useState<number | null>(null);
  const [cursoVistaNombre, setCursoVistaNombre]   = useState('');

  const { user } = useAuth();
  const soloLectura = user?.rol === 'DIRECTOR';

  const { getValor } = useConfigInstitucional();
  const usaCompetencias = getValor('EVAL_USA_COMPETENCIAS') === 'true';

  const [cursos, setCursos]     = useState<Curso[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);

  // Filtros
  const [search, setSearch]             = useState('');
  const [activoFilter, setActivoFilter] = useState('');

  // Eliminación
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Cargar lista ──────────────────────────────────────────
  const loadCursos = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await cursoService.listar();
      setCursos(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar cursos');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCursos(); }, [loadCursos]);

  const handleSuccess = (cursoId?: number, nombre?: string) => {
    loadCursos();
    if (cursoId && usaCompetencias) {
      setCursoCreadoId(cursoId);
      setCursoCreadoNombre(nombre ?? 'nuevo curso');
      setVista('competencias');
    } else {
      setVista('list');
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await cursoService.eliminar(deleteId);
      setDeleteId(null);
      loadCursos();
    } catch { /* httpClient lanza el error */ }
    finally { setDeleting(false); }
  };

  // ── Filtrado en cliente ───────────────────────────────────
  const cursosFiltrados = cursos.filter(c => {
    const matchSearch = search === '' ||
      c.nombre.toLowerCase().includes(search.toLowerCase()) ||
      c.descripcion.toLowerCase().includes(search.toLowerCase());
    const matchActivo = activoFilter === '' || String(c.activo) === activoFilter;
    return matchSearch && matchActivo;
  });

  // ── Ver competencias de un curso existente ─────────────────
  if (vista === 'ver-competencias' && cursoVistaId) {
    return (
      <PasoCompetencias
        cursoId={cursoVistaId}
        cursoNombre={cursoVistaNombre}
        modo="ver"
        readOnly={soloLectura}
        onFinish={() => setVista('list')}
        onBack={() => setVista('list')}
      />
    );
  }

  // ── Paso 2: competencias tras crear ───────────────────────
  if (vista === 'competencias' && cursoCreadoId) {
    return (
      <PasoCompetencias
        cursoId={cursoCreadoId}
        cursoNombre={cursoCreadoNombre}
        onFinish={() => setVista('list')}
      />
    );
  }

  // ── Formulario crear / editar ─────────────────────────────
  if (vista === 'new' || vista === 'edit') {
    return (
      <CursoFormPage
        idCurso={vista === 'edit' ? editId! : undefined}
        onSuccess={handleSuccess}
        onCancel={() => setVista('list')}
      />
    );
  }

  // ── Vista lista ───────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Gestión de Cursos</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {cursos.length} curso{cursos.length !== 1 ? 's' : ''} registrado{cursos.length !== 1 ? 's' : ''}
          </p>
        </div>
        {!soloLectura && (
          <button onClick={() => setVista('new')}
            className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                       font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
            <Ico d={D_PLUS} /> Registrar Curso
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <Ico d={D_SEARCH} />
            </span>
            <input type="text" placeholder="Buscar por nombre o descripción..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50
                         text-sm text-slate-800 placeholder-slate-300 outline-none
                         focus:ring-2 focus:ring-blue-500 transition-all" />
          </div>
          <select value={activoFilter}
            onChange={e => setActivoFilter(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px]">
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16"><SpinnerIcon /></div>
        ) : error ? (
          <div className="text-center py-16 text-red-500 text-sm">{error}</div>
        ) : cursosFiltrados.length === 0 ? (
          <div className="text-center py-16">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
              className="w-12 h-12 text-slate-200 mx-auto mb-3">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342"/>
            </svg>
            <p className="text-slate-400 text-sm font-medium">No se encontraron cursos</p>
            <p className="text-slate-300 text-xs mt-1">Cambia los filtros o registra uno nuevo</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {['Curso', 'Descripción', 'Estado', 'Acciones'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {cursosFiltrados.map(c => (
                  <tr key={c.idCurso} className="hover:bg-slate-50 transition-colors">

                    {/* Nombre */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center
                                        bg-blue-100 text-blue-700 font-bold text-xs">
                          {c.nombre.substring(0, 2).toUpperCase()}
                        </div>
                        <p className="font-semibold text-slate-800 text-sm">{c.nombre}</p>
                      </div>
                    </td>

                    {/* Descripción */}
                    <td className="px-4 py-3.5 text-sm text-slate-500 max-w-xs">
                      <p className="truncate">{c.descripcion}</p>
                    </td>

                    {/* Estado */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full
                        ${c.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${c.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {c.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-0.5">
                        <ActionBtn title="Ver competencias" color="purple"
                          onClick={() => { setCursoVistaId(c.idCurso); setCursoVistaNombre(c.nombre); setVista('ver-competencias'); }}>
                          <Ico d={D_LIST} />
                        </ActionBtn>
                        {!soloLectura && (
                          <>
                            <ActionBtn title="Editar curso" color="blue"
                              onClick={() => { setEditId(c.idCurso); setVista('edit'); }}>
                              <Ico d={D_EDIT} />
                            </ActionBtn>
                            <ActionBtn title="Eliminar curso" color="red"
                              onClick={() => setDeleteId(c.idCurso)}>
                              <Ico d={D_DEL} />
                            </ActionBtn>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Contador de resultados filtrados */}
        {!loading && cursosFiltrados.length > 0 && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
            <p className="text-xs text-slate-400">
              Mostrando {cursosFiltrados.length} de {cursos.length} curso{cursos.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Modal confirmación de eliminación */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="bg-red-50 border-b border-red-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                  <Ico d={D_DEL} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Eliminar Curso</h3>
                  <p className="text-slate-400 text-xs">
                    {cursos.find(c => c.idCurso === deleteId)?.nombre}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <p className="text-slate-600 text-sm">
                ¿Estás seguro? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setDeleteId(null)} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold
                           text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDelete} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-sm font-bold
                           text-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {deleting && (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                  </svg>
                )}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CursosPage;
